




















































































import { defineComponent, computed, ref, onBeforeUnmount, watch } from '@vue/composition-api';
import type { Channel } from '@getaccept/pusher';
import { ChannelType, UserEvent, ConnectionState } from '@getaccept/pusher';
import { Settings } from 'luxon';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import { getGlobalHandle, throttle } from '@getaccept/lib-shared/src/helpers';
import { AnalyticsEvent } from '@getaccept/lib-shared/src/types/analytics';
import type { SelectEntity } from '@getaccept/lib-shared/src/select-entity/types/select-entity';
import Analytics from '@getaccept/lib-shared/src/analytics';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';
import { LocalStorageKey } from '@getaccept/lib-shared/src/enums/local-storage';
import { LocalStorageHelper } from '@getaccept/lib-shared/src/helpers/local-storage.helper';
import { EventBusEvent } from '@getaccept/lib-shared/src/event-bus/event-bus-event';
import EventBus from '@getaccept/lib-shared/src/event-bus/event-bus';
import { Toasts, useToast } from '@getaccept/lib-shared/src/toast';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { RouterHelper } from '@getaccept/lib-shared/src/helpers/router.helper';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Route } from 'vue-router';
import { setLocale as setYupLocale } from 'yup';
import { ModuleName } from '@getaccept/lib-shared/src/entity/app-modules/enums/app-module';
import { storeToRefs } from 'pinia';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { useAuthStore } from '@getaccept/lib-shared/src/internal/session/store/auth.store';
import { pusherClientInternal } from '@getaccept/lib-shared/src/internal/pusher';
import { useLocalStorage, useStorage } from '@vueuse/core';
import BugsnagPerformance from '@bugsnag/browser-performance';
import EntityChangedModal from '@getaccept/lib-shared/src/session/components/EntityChangedModal.vue';
import { getTemplateIdFromRegistrationCampaign, hasValidString } from './common/helpers';
import { getTimezone } from './common/timezone/timezone.helper';
import { useSessionStore } from './session/store/session.store';
import IntersectionModal from './onboarding/components/IntersectionModal.vue';
import { loadLanguageAsync } from './lang';
import AccessDeniedModal from './access-denied/AccessDeniedModal.vue';
import SubscriptionInfoModal from './subscription/components/SubscriptionInfoModal.vue';
import SubscriptionWelcomeModal from './subscription/components/SubscriptionWelcomeModal.vue';
import GlobalSidebar from './sidebar/components/GlobalSidebar.vue';
import LoadProductScreen from './sidebar/components/LoadProductScreen.vue';
import TemplateLoader from './template-gallery/components/TemplateLoaderModal.vue';
import GlobalNavMenu from './topbar/components/GlobalNavMenu.vue';
import GlobalTopbar from './topbar/components/GlobalTopbar.vue';
import DsrTemplatePicker from './dsr-template-picker/DsrTemplatePicker.vue';
import { useUserStore } from './user/store/user.store';
import { useEntityStore } from './entity/entity.store';
import router from './router/router';
import { useTemplatePicker } from './dsr-template-picker/store/template-picker.store';
import { yupLocalizationKeys } from './common/yup-localization-keys';
import { useAccessDeniedModalStore } from './access-denied/access-denied-modal.store';
import { useModulesStore } from './modules/store/modules.store';
import { useSubscriptionPusher } from './subscription/composables/subscription-pusher.composable';
import UpdatedAccessModal from './subscription/components/UpdatedAccessModal.vue';
import AppLayout from './AppLayout.vue';
import { useNavigationMenuStore } from './topbar/store/navigation-menu.store';
import { DeviceIdHelper } from './helpers/device-id.helper';
import { useSubscriptionStore } from './subscription/store/subscription.store';

export default defineComponent({
  components: {
    AppLayout,
    GlobalSidebar,
    GlobalNavMenu,
    GlobalTopbar,
    AccessDeniedModal,
    SubscriptionInfoModal,
    SubscriptionWelcomeModal,
    IntersectionModal,
    TemplateLoader,
    EntityChangedModal,
    DsrTemplatePicker,
    LoadProductScreen,
    Toasts,
    UpdatedAccessModal,
  },
  setup(_, { root }) {
    const { toast } = useToast();
    const showRouterView = ref(false);
    const cachedViews = ref([]);
    const loadProductScreenVisible = ref(false);
    const templatePickerStore = useTemplatePicker();
    const { isVisible: dsrTemplatePickerVisible } = storeToRefs(templatePickerStore);
    const showTemplateLoader = ref(false);
    const templateId = ref(null);
    const entityChanged = ref(false);
    const onboardingVisible = ref(false);
    const subscriptionStore = useSubscriptionStore();
    const { setAccessChanged } = subscriptionStore;
    // eslint-disable-next-line no-restricted-syntax
    const { isInactiveSubscription, isTrialPlan, daysLeft, accessChanged } = storeToRefs(
      subscriptionStore
    );
    const { flags } = useLaunchDarkly();
    if (flags.value.newSubscription) {
      useSubscriptionPusher();
    }
    const { toggleAccessDeniedModal } = useAccessDeniedModalStore();
    const { showAccessDeniedModal } = storeToRefs(useAccessDeniedModalStore());
    const { isOpen: isGlobalNavMenuOpen } = storeToRefs(useNavigationMenuStore());
    const { setOpen: setGlobalNavMenuVisibility } = useNavigationMenuStore();
    const { isXSmallScreenAndDown, isSmallScreenAndDown, isSmallScreenAndUp } = useBreakpoints();

    const entityStore = useEntityStore();
    const sessionStore = useSessionStore();
    const authStore = useAuthStore();
    const userStore = useUserStore();
    const modulesStore = useModulesStore();

    const performanceFlag = computed(() => flags.value.cmSearchV2);
    const useKeepAlive = computed(
      () => performanceFlag.value && process.env.NODE_ENV !== 'development'
    );

    const userRole = computed(() => sessionStore.session?.user_role || null);
    const isDsrEdit = computed(() => root.$route.path.includes('/dsr-edit'));
    const showIntroPaths = computed(
      () =>
        router.currentRoute.path === RouterHelper.dashboardPath || router.currentRoute.path === '/'
    );

    const isMyDocumentRelogin = computed(
      () => router.currentRoute.path === '/my-documents-redirect'
    ); // TODO add restriction

    // SetupWizard will be removed after a grace period
    const introEligiblePlan = computed(() => false);

    const subscriptionWelcomeModalShown = useLocalStorage(
      `GA_SUBSCRIPTION_WELCOME_MODAL_SHOWN_${userStore.user.id}`,
      false
    );

    const showSubscriptionInfoModal = ref(subscriptionWelcomeModalShown.value);
    const subscriptionModalTriggeredByUser = ref(false);
    // eslint-disable-next-line no-restricted-syntax
    const isActiveTrialPlan = computed(() => isTrialPlan.value && !isInactiveSubscription.value);

    const openSubscriptionModal = () => {
      showSubscriptionInfoModal.value = true;
      subscriptionModalTriggeredByUser.value = true;
    };

    const modalDescription = computed(() =>
      subscriptionModalTriggeredByUser.value
        ? t('trial_plan_learn_more_modal_info')
        : t('trial_reload_message')
    );

    const showIntro = computed(
      () =>
        (userStore.user?.nbrDocumentsSent < 1 && introEligiblePlan.value && showIntroPaths.value) ||
        'intro' in router.currentRoute.query
    );

    const updateLanguage = () => {
      loadLanguageAsync(sessionStore.session.language);
      setLocale();
    };

    watch(() => sessionStore.session.language, updateLanguage);

    const closeOnboarding = () => {
      onboardingVisible.value = false;
    };

    const handleCloseAccessDeniedModal = () => {
      toggleAccessDeniedModal(false);
    };

    const handleCloseTemplateLoader = () => {
      showTemplateLoader.value = false;
    };

    const initiateAnalytics = () => {
      if (getGlobalHandle().Intercom) {
        getGlobalHandle().Intercom('update', {
          hide_default_launcher: true,
          language_override: sessionStore.session.language,
          user_hash: sessionStore.session.iio_hash,
        });
      }

      const analyticsScriptLoaded =
        // eslint-disable-next-line no-prototype-builtins
        !!getGlobalHandle().pendo || !!getGlobalHandle().analytics.hasOwnProperty('initialized');

      if (analyticsScriptLoaded) {
        Analytics.initializeCoreApp(sessionStore.session, userStore.user, entityStore.entity);
      } else {
        setTimeout(() => {
          Analytics.initializeCoreApp(sessionStore.session, userStore.user, entityStore.entity);
        }, 10000);
      }
    };

    const setLocale = () => {
      Settings.defaultLocale = sessionStore.session.language;
      Settings.defaultZone = getTimezone();
      setYupLocale(yupLocalizationKeys);
    };

    const setBugsnagMetadata = () => {
      if (!bugsnagClient || !sessionStore.session) {
        return;
      }
      bugsnagClient.metaData = {
        entity: {
          id: sessionStore.session.entity_hash,
          name: sessionStore.session.entity_name,
        },
        user: {
          id: sessionStore.session.user_hash,
        },
      };
    };

    const handleSelectEntity = (entity: SelectEntity) => {
      if (entity.auth) {
        entityStore.authEntity(entity);
        return;
      }
      entityStore.selectEntity(entity.id);
    };

    const addListeners = async () => {
      userStore.pusherChannel = await pusherClientInternal.subscribe(ChannelType.User);
      entityStore.pusherChannel = await pusherClientInternal.subscribe(ChannelType.Entity);

      pusherClientInternal.connectionListen(ConnectionState.Error, e => {
        const pusherErrors = [1006, 4201, 4200];
        if (
          pusherErrors.includes(e.data?.code) ||
          pusherErrors.includes(e.error?.data?.code) ||
          (e.error && Object.prototype.hasOwnProperty.call(e.error, 'isTrusted')) ||
          Object.prototype.hasOwnProperty.call(e, 'isTrusted')
        ) {
          return;
        }
        bugsnagClient.notify(e);
      });

      pusherClientInternal.listen(
        userStore.pusherChannel as Channel,
        UserEvent.UploadContactsDone,
        () => {
          toast.success(t('upload-contacts-success-alert-description'));
        }
      );

      window.addEventListener('unhandledrejection', unhandledrejectionHandler);
      window.addEventListener('storage', handleStorageChange);
    };

    const handleStorageChange = () => {
      const entityId: string = localStorage.getItem('GA_ACTIVE_ENTITY');
      if (entityId && sessionStore.session.entity_hash !== entityId) {
        entityChanged.value = true;
      }
      generateDeviceId();
    };

    const handleReload = () => {
      location.replace('/');
    };

    const unhandledrejectionHandler = event => {
      if (bugsnagClient) {
        bugsnagClient.notify(event);
      }
    };

    const handleTemplateIdRegistrationCampaign = () => {
      let templateIdFromRegistrationCampaign = null;

      if (sessionStore.session?.registration_campaign?.includes('templateid_')) {
        templateIdFromRegistrationCampaign = getTemplateIdFromRegistrationCampaign(
          sessionStore.session.registration_campaign
        );
      }
      if (hasValidString(templateId.value)) {
        router.replace(RouterHelper.dashboardPath);
        templateId.value = templateIdFromRegistrationCampaign;
        showTemplateLoader.value = true;
      } else {
        handleShowIntro();
      }
    };

    const handleShowIntro = () => {
      if (!router.currentRoute.query) {
        router.replace('/dashboard?intro');
      }
    };

    const showLoadProductScreen = async () => {
      loadProductScreenVisible.value = true;
      await new Promise(resolve => setTimeout(resolve, 1200));
      loadProductScreenVisible.value = false;
    };

    const closeMobileGlobalNav = () => {
      if (isXSmallScreenAndDown.value) {
        setTimeout(() => setGlobalNavMenuVisibility(false), 1000);
      }
    };

    const handleSetModuleType = async (module: ModuleName) => {
      closeMobileGlobalNav();
      await showLoadProductScreen();

      modulesStore.setActiveModule(module);

      switch (module) {
        case ModuleName.DealRooms:
          router.push({ name: PathName.Rooms });
          return;
        case ModuleName.Contracts:
          router.push({ name: PathName.Dashboard });
          return;
        default:
          router.push({ name: PathName.Root });
      }
    };

    const emitScrollEvent = (event: EventBusEvent) => {
      EventBus.emit(event);
    };

    const throttleEmitScroll = throttle(emitScrollEvent, 500);

    const handleMainContentScroll = (event: Event) => {
      const mainContent = event.target as HTMLElement;
      const scrolledToTop: boolean = mainContent.scrollTop === 0;
      const scrolledTobottom: boolean =
        mainContent.offsetHeight + Math.floor(mainContent.scrollTop) === mainContent.scrollHeight;

      if (scrolledToTop) {
        throttleEmitScroll(EventBusEvent.MainContentScrolledToTop);
      } else if (scrolledTobottom) {
        throttleEmitScroll(EventBusEvent.MainContentScrolledToBottom);
      }
    };

    const handleDocumentCreated = ({ id, type }: { id: string; type: EnumDocumentType }) => {
      if (type === EnumDocumentType.DSR) {
        dsrTemplatePickerVisible.value = false;
        router.push({
          name: PathName.DsrEdit,
          params: { id },
        });
        closeMobileGlobalNav();
        return;
      }
      Analytics.track(AnalyticsEvent.CreatedDocument, {
        documentType: type,
      });
      router.push(`/document/edit/${id}`);
      closeMobileGlobalNav();
    };

    const handleUpdatedAccess = () => {
      setAccessChanged(false);
      window.location.replace(window.location.href);
    };

    const handleComponentCache = (to: Route, from?: Route) => {
      if (
        (to.name !== PathName.Contracts && !cachedViews.value.length) ||
        (to.name === PathName.Contracts && from?.name === PathName.Contracts)
      ) {
        return;
      }
      cachedViews.value = to.name === PathName.Contracts ? ['Contracts'] : [];
    };

    const initiateRouterGuards = () => {
      router.beforeEach((to: Route, from: Route, next) => {
        if (performanceFlag.value) {
          handleComponentCache(to, from);
        }
        next();
      });

      router.afterEach((to: Route) => {
        if ('intro' in to.query) {
          onboardingVisible.value = true;
        }
      });

      if ('intro' in router.currentRoute.query) {
        onboardingVisible.value = true;
      }
    };

    const preventIosInputZoom = () => {
      if (navigator.userAgent.indexOf('iPhone') > -1) {
        document
          .querySelector('[name=viewport]')
          .setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
      }
    };

    const deviceId = useStorage<string>('deviceId', DeviceIdHelper.generate());
    const generateDeviceId = () => {
      if (!deviceId.value) {
        deviceId.value = DeviceIdHelper.generate();
      }
    };

    const initBugSnagPerformanceMonitoring = () => {
      if (process.env.VUE_APP_BUGSNAG) {
        BugsnagPerformance.start({
          apiKey: process.env.VUE_APP_BUGSNAG,
          releaseStage: process.env.VUE_APP_BUGSNAG_RELEASE_STAGE || 'development',
          appVersion: process.env.VUE_APP_BUGSNAG_APP_VERSION || '0.0.0',
        });
      }
    };

    const init = async () => {
      preventIosInputZoom();
      try {
        await Promise.all([
          loadLanguageAsync(sessionStore.session.language),
          pusherClientInternal.connect(authStore.token),
        ]);
      } catch (e) {
        bugsnagClient?.notify(e);
      }

      initiateRouterGuards();
      setLocale();
      setBugsnagMetadata();
      initiateAnalytics();
      generateDeviceId();

      try {
        await addListeners();
      } catch (e) {
        bugsnagClient?.notify(e);
      }

      initBugSnagPerformanceMonitoring();

      showRouterView.value = true;

      templateId.value = LocalStorageHelper.getLocalStorage(LocalStorageKey.Template);
      if (templateId.value) {
        showTemplateLoader.value = true;
      } else if (showIntro.value) {
        handleTemplateIdRegistrationCampaign();
      }
    };

    init();

    onBeforeUnmount(() => {
      window.removeEventListener('unhandledrejection', unhandledrejectionHandler);
      window.removeEventListener('storage', handleStorageChange);
    });

    return {
      showAccessDeniedModal,
      entityStore,
      sessionStore,
      userStore,
      showRouterView,
      loadProductScreenVisible,
      dsrTemplatePickerVisible,
      modulesStore,
      showTemplateLoader,
      templateId,
      isGlobalNavMenuOpen,
      entityChanged,
      onboardingVisible,
      userRole,
      isDsrEdit,
      isMyDocumentRelogin,
      closeOnboarding,
      setGlobalNavMenuVisibility,
      handleCloseAccessDeniedModal,
      handleCloseTemplateLoader,
      handleSelectEntity,
      handleReload,
      handleSetModuleType,
      handleMainContentScroll,
      handleDocumentCreated,
      router,
      PathName,
      cachedViews,
      useKeepAlive,
      showSubscriptionInfoModal,
      subscriptionWelcomeModalShown,
      daysLeft,
      accessChanged,
      handleUpdatedAccess,
      isSmallScreenAndDown,
      isSmallScreenAndUp,
      isActiveTrialPlan,
      openSubscriptionModal,
      modalDescription,
    };
  },
});
